/*
 * Copyright (c) 2026. Bluemate B.V.
 */
import './style/oauthAuthorize.scss';

import {ErrorType, Loader} from '@dvrd/dvr-controls';

import React, {useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {normalizeLanguage} from '../../../i18n/config';

import PortalSelectController from '../../controls/portalSelect/portalSelectController';

import {BluemateButton} from '../../controls';

import ConnectSteps from '../../controls/connectSteps/connectSteps';

import Image from '../../misc/image';

import ConnectSignInController from './connectSignInController';

export type ConnectView = 'resolving' | 'chooser' | 'signIn' | 'authorizing' | 'cancelling' | 'done' | 'error';

export interface Props {
    view: ConnectView;
    account: string | null;
    error: ErrorType;
    canRetry: boolean;
    onContinue: VoidFunction;
    onSwitchAccount: VoidFunction;
    onCancel: VoidFunction;
    onRetry: VoidFunction;
    onReturn: VoidFunction;
    onSignedIn: VoidFunction;
}

// The whole platform side of the MCP connect hand-off as one standalone card:
// no app chrome, the same base layout as the MCP consent page, and a step bar
// carried across both services. Sub-views swap inside the card — the browser
// never navigates until the final redirect.
export default function OAuthAuthorize(props: Props) {
    const {view, account, error, canRetry, onContinue, onSwitchAccount, onCancel, onRetry, onReturn, onSignedIn} =
        props;
    const {t, i18n} = useTranslation();
    const language = normalizeLanguage(i18n.language);
    const languageItems = useMemo(
        () => [
            {
                value: 'nl',
                label: (
                    <span>
                        <span className='fi fi-nl' style={{margin: '0 0.375rem'}} />{' '}
                        {t('settings.language.nl', 'Nederlands')}
                    </span>
                )
            },
            {
                value: 'en',
                label: (
                    <span>
                        <span className='fi fi-gb' style={{margin: '0 0.375rem'}} />{' '}
                        {t('settings.language.en', 'Engels')}
                    </span>
                )
            }
        ],
        [t]
    );

    function onChangeLanguage(nextLanguage: string | null) {
        if (!nextLanguage) return;
        const normalized = normalizeLanguage(nextLanguage);
        if (normalized !== 'nl' && normalized !== 'en') return;
        void i18n.changeLanguage(normalized);
    }

    const stepLabels: [string, string, string] = [
        t('oauth.authorize.step1', 'Toegang'),
        t('oauth.authorize.step2', 'Account'),
        t('oauth.authorize.step3', 'Klaar')
    ];

    function renderCancel() {
        return (
            <div className='connect-leave'>
                <span>{t('oauth.authorize.cancelQuestion', 'Wil je je AI-assistent toch niet koppelen?')}</span>
                <button type='button' className='connect-leave-action' onClick={onCancel}>
                    {t('oauth.authorize.cancelAction', 'Ga terug')}
                </button>
            </div>
        );
    }

    function renderReturn() {
        return (
            <div className='connect-actions'>
                <BluemateButton
                    onClick={onReturn}
                    label={t('oauth.authorize.returnToApp', 'Terug naar je AI-assistent')}
                    align='center'
                />
            </div>
        );
    }

    function renderChooser() {
        const continueLabel = account
            ? t('oauth.authorize.continueAs', 'Doorgaan als {{account}}', {account})
            : t('oauth.authorize.continue', 'Doorgaan met het ingelogde account');
        return (
            <>
                <p className='connect-prose centered'>
                    {account
                        ? t(
                              'oauth.authorize.signedInAs',
                              'Je bent ingelogd als {{account}}. Ga verder met dit account of log in met een ander account.',
                              {account}
                          )
                        : t(
                              'oauth.authorize.signedInUnnamed',
                              'Je bent ingelogd. Ga verder met dit account of log in met een ander account.'
                          )}
                </p>
                <div className='connect-actions'>
                    <BluemateButton onClick={onContinue} label={continueLabel} align='center' />
                    <BluemateButton
                        onClick={onSwitchAccount}
                        label={t('oauth.authorize.switchAccount', 'Inloggen met een ander account')}
                        tertiary
                        align='center'
                    />
                </div>
                {renderCancel()}
            </>
        );
    }

    function renderSignIn() {
        return (
            <>
                <p className='connect-prose centered'>
                    {t('oauth.authorize.signInIntro', 'Log in met je Bluemate-account om de koppeling af te ronden.')}
                </p>
                <div className='connect-sign-in'>
                    <ConnectSignInController onSignedIn={onSignedIn} />
                </div>
                {renderCancel()}
            </>
        );
    }

    function renderBusy(label?: string) {
        return (
            <div className='connect-busy'>
                <Loader position='relative' active disableBackground />
                {!!label && <label>{label}</label>}
            </div>
        );
    }

    function renderDone() {
        return (
            <>
                <p className='connect-prose centered'>
                    {t(
                        'oauth.authorize.doneProse',
                        'De koppeling is succesvol ingesteld. Je wordt automatisch teruggestuurd naar je AI-assistent.'
                    )}
                </p>
                {renderReturn()}
            </>
        );
    }

    function renderError() {
        return (
            <>
                <p className='connect-prose centered'>
                    {error === 'invalidRequest'
                        ? t(
                              'oauth.authorize.invalidRequest',
                              'Deze koppelingsaanvraag is onvolledig. Start de koppeling opnieuw vanuit je AI-assistent.'
                          )
                        : t(
                              'oauth.authorize.failed',
                              'Het koppelen met de externe applicatie is niet gelukt. Probeer het opnieuw.'
                          )}
                </p>
                {canRetry && (
                    <div className='connect-actions'>
                        <BluemateButton
                            onClick={onRetry}
                            label={t('oauth.authorize.retry', 'Opnieuw proberen')}
                            align='center'
                        />
                    </div>
                )}
                {renderReturn()}
            </>
        );
    }

    function renderBody() {
        if (view === 'chooser') return renderChooser();
        if (view === 'signIn') return renderSignIn();
        if (view === 'authorizing')
            return renderBusy(t('oauth.authorize.statusConnecting', 'Verbinding maken met Bluemate MCP...'));
        if (view === 'cancelling') return renderBusy();
        if (view === 'done') return renderDone();
        if (view === 'error') return renderError();
        return renderBusy(t('oauth.authorize.resolving', 'Eén moment...'));
    }

    function title(): string {
        if (view === 'cancelling') return t('oauth.authorize.statusCancelling', 'Annuleren...');
        if (view === 'signIn') return t('oauth.authorize.signInTitle', 'Log in om te koppelen');
        if (view === 'done') return t('oauth.authorize.statusSuccess', 'Verbinding gelukt!');
        if (view === 'error') return t('oauth.authorize.statusError', 'Verbinden mislukt');
        return t('oauth.authorize.title', 'Koppeling met Bluemate MCP-server');
    }

    return (
        <div className='connect-page'>
            <div className='connect-language'>
                <PortalSelectController value={language} onChange={onChangeLanguage} items={languageItems} />
            </div>
            <main className='connect-card'>
                <Image name='logo' className='connect-logo' />
                <ConnectSteps current={view === 'done' ? 3 : 2} labels={stepLabels} />
                <h1 className='connect-title'>{title()}</h1>
                {renderBody()}
            </main>
        </div>
    );
}
